import { ref, watch } from 'vue';
import { useGlobalState } from '@/composables/useGlobalState';
import { useToast } from '@/composables/useToast';

export type SportSessionParticipant = {
    id: number;
    name: string;
    isShared: boolean;
};

export type SportSession = {
    uid: string;
    title: string;
    description: string;
    cancelUrl: string | null;
    start: string;
    end: string;
    provider: 'episod' | 'sanctuary';
    isCancelled: boolean;
    ownerId: number;
    ownerName: string;
    isShared: boolean;
    participants: SportSessionParticipant[];
};

const sessions = ref<{ needsSync?: boolean; upcoming: SportSession[]; past: SportSession[] } | null>(null);
const sessionsLoading = ref(false);
const isSyncing = ref(false);
const sessionsError = ref(false);

export function useSportSessions() {
    const { getAuthHeader, impersonatedUser } = useGlobalState();
    const { addToast } = useToast();

    // Clear cache when impersonation status changes
    watch(impersonatedUser, () => {
        sessions.value = null;
    });

    const mergeSessions = (rawSessions: any[]): SportSession[] => {
        const merged = new Map<string, SportSession>();
        for (const s of rawSessions) {
            if (!s.uid) continue; // Ignore sessions without UID

            if (merged.has(s.uid)) {
                const existing = merged.get(s.uid)!;
                // Add owner to a list of participants
                if (!existing.participants.some(p => p.id === s.ownerId)) {
                    existing.participants.push({ id: s.ownerId, name: s.ownerName, isShared: s.isShared });
                }
                // If one of the sessions is owned by the current user, the merged session should not be "shared"
                if (!s.isShared) {
                    existing.isShared = false;
                    existing.cancelUrl = s.cancelUrl || existing.cancelUrl;
                }
            } else {
                const cloned = { ...s };
                cloned.participants = [{ id: s.ownerId, name: s.ownerName, isShared: s.isShared }];
                merged.set(s.uid, cloned);
            }
        }
        return Array.from(merged.values()).sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    };

    const processResponse = async (response: Response) => {
        const data = await response.json();
        return {
            needsSync: data.needsSync,
            upcoming: mergeSessions(data.upcoming),
            past: mergeSessions(data.past),
        };
    };

    const triggerBackgroundSync = async () => {
        if (isSyncing.value) return;
        isSyncing.value = true;
        try {
            const response = await fetch('/api/sport/sessions/sync', {
                method: 'POST',
                headers: getAuthHeader()
            });
            if (response.ok) {
                sessions.value = await processResponse(response);
            }
        } catch (e) {
            console.error('Erreur lors de la synchronisation en arrière-plan', e);
        } finally {
            isSyncing.value = false;
        }
    };

    const loadSessions = async () => {
        if (sessions.value !== null) return;
        sessionsLoading.value = true;
        sessionsError.value = false;
        try {
            const response = await fetch('/api/sport/sessions', {
                headers: getAuthHeader()
            });
            if (!response.ok) throw new Error();
            sessions.value = await processResponse(response);

            if (sessions.value?.needsSync) {
                triggerBackgroundSync();
            }
        } catch (e) {
            sessionsError.value = true;
            addToast('Erreur lors du chargement des sessions', 'error');
        } finally {
            sessionsLoading.value = false;
        }
    };

    const refreshSessions = async () => {
        if (isSyncing.value) return;
        isSyncing.value = true;
        try {
            const response = await fetch('/api/sport/sessions/sync', {
                method: 'POST',
                headers: getAuthHeader()
            });
            if (!response.ok) throw new Error();
            sessions.value = await response.json();
            addToast('Vos sessions ont été synchronisées', 'success');
        } catch (e) {
            addToast('Erreur lors du rafraîchissement des sessions', 'error');
        } finally {
            isSyncing.value = false;
        }
    };

    const clearSessionsCache = () => {
        sessions.value = null;
    };

    return {
        sessions,
        sessionsLoading,
        isSyncing,
        sessionsError,
        triggerBackgroundSync,
        loadSessions,
        refreshSessions,
        clearSessionsCache,
    };
}
